import React from "react";
import { useParams } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useLanguage } from '../i18n/translations';
import apiUrl from "../components/Url";
import ThankYouForOrdering from "../components/ThankYouForOrdering";
import Footer from "../components/Footer";
import { pixelPurchase } from "../components/MetaPixel";
import { useCardPayment, getStripe, stripeLocale, paymentReturnUrl, orderPixelPurchase, loadMonri, monriLocale } from "../components/cardPayment";

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
}

function CardForm({ orderId, amount, currency, error, setError, onConfirmed, translate }) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = React.useState(false);
  const [isPaying, setIsPaying] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || isPaying) return;

    setIsPaying(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: paymentReturnUrl(orderId) },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed. Please try again.');
      setIsPaying(false);
    } else if (paymentIntent && ['succeeded', 'processing'].includes(paymentIntent.status)) {
      onConfirmed();
    } else {
      setError('Payment failed. Please try again.');
      setIsPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="font-sans">
      <PaymentElement options={{ wallets: { link: 'never' } }} onReady={() => setReady(true)} />
      {error && <p className="text-red-500 mt-4">{translate(error)}</p>}
      <button
        type="submit"
        disabled={!stripe || !ready || isPaying}
        className={`w-full px-6 py-3 rounded-full font-medium transition-all duration-300 mt-6 text-center ${
          !stripe || !ready || isPaying
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-darkerSkin text-white hover:bg-[#d4a76a] hover:shadow-lg'
        }`}
      >
        {isPaying ? (
          <span className="flex items-center justify-center gap-2 text-white"><Spinner />{translate("Processing...")}</span>
        ) : `${translate("Pay")} ${Number(amount).toFixed(2)} ${currency}`}
      </button>
    </form>
  );
}

// Monri's card component. A declined card ends that Monri payment, so on failure we keep
// the message and ask the backend for a fresh payment (onRetry) before the next try.
function MonriCardForm({ checkout, language, error, setError, onConfirmed, onRetry, translate }) {
  const monri = React.useRef(null);
  const card = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [isPaying, setIsPaying] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    loadMonri(checkout.scriptUrl).then(Monri => {
      if (cancelled) return;
      monri.current = Monri(checkout.authenticityToken, { locale: monriLocale(language) });
      card.current = monri.current
        .components({ clientSecret: checkout.clientSecret })
        .create('card', { style: { base: { fontSize: '16px', color: '#1f2937' } } });
      card.current.mount('monri-card-element');
      card.current.onChange(event => setError(event.error ? event.error.message : null));
      setReady(true);
    }).catch(() => {
      if (!cancelled) setError('Card payment is unavailable right now. Please try again later or contact us on Instagram.');
    });
    return () => {
      cancelled = true;
      const el = document.getElementById('monri-card-element');
      if (el) el.innerHTML = '';
    };
  }, [checkout, language, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ready || isPaying) return;

    setIsPaying(true);
    setError(null);

    try {
      const { result, error: monriError } = await monri.current.confirmPayment(card.current, {
        ...checkout.customer,
        orderInfo: checkout.orderInfo,
      });
      if (!monriError && result?.status === 'approved') {
        onConfirmed();
        return;
      }
      setError(monriError?.message || 'Payment failed. Please try again.');
    } catch (_) {
      setError('Payment failed. Please try again.');
    }
    onRetry();
  };

  return (
    <form onSubmit={handleSubmit} className="font-sans">
      <div id="monri-card-element" className="rounded-lg border border-gray-300 bg-white p-3" />
      {error && <p className="text-red-500 mt-4">{translate(error)}</p>}
      <button
        type="submit"
        disabled={!ready || isPaying}
        className={`w-full px-6 py-3 rounded-full font-medium transition-all duration-300 mt-6 text-center ${
          !ready || isPaying
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-darkerSkin text-white hover:bg-[#d4a76a] hover:shadow-lg'
        }`}
      >
        {isPaying ? (
          <span className="flex items-center justify-center gap-2 text-white"><Spinner />{translate("Processing...")}</span>
        ) : `${translate("Pay")} ${Number(checkout.amount).toFixed(2)} ${checkout.currency}`}
      </button>
    </form>
  );
}

export default function Payment({ translate, country, currency, productsData, categories, RemoveLocalStorage }) {
  const { orderId } = useParams();
  const { language } = useLanguage();
  const { phase, checkout, order, error, setError, waitForWebhook, reload } = useCardPayment(apiUrl, orderId);

  React.useEffect(() => {
    if (phase === 'paid' && order) orderPixelPurchase(order, pixelPurchase);
  }, [phase, order]);

  // A bank redirect (3-D Secure) leaves the checkout before it can empty the cart
  const paid = phase === 'paid';
  React.useEffect(() => {
    if (paid) RemoveLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid]);

  if (phase === 'paid' && order) {
    return (
      <div>
        <div className='h-[50px] w-full 768:h-[100px] '></div>
        <ThankYouForOrdering translate={translate} country={country} currency={order.currency || currency} apiData={order} productsData={productsData} />
        <Footer categories={categories} />
      </div>
    );
  }

  const message = {
    processing: "Payment is being processed. You will receive a confirmation email once it is completed.",
    canceled: "This payment was canceled. Please contact us on Instagram.",
    error,
  }[phase];

  return (
    <div>
      <div className='h-[50px] w-full 768:h-[100px] '></div>
      <div className="flex justify-center px-4 pb-20">
        <div className="w-full max-w-[500px] font-sans">
          <h2 className="text-xl font-medium">{translate("Card payment")}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {translate("Your order is saved and will be confirmed once the payment is completed.")}
          </p>

          <div className="rounded-2xl bg-bgSkin p-5 mt-6 space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{translate("Order ID")}</span>
              <span className="font-mono text-gray-800 break-all text-right">{orderId}</span>
            </div>
            {checkout && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">{translate("Amount due")}</span>
                <span className="font-semibold text-gray-800">{Number(checkout.amount).toFixed(2)} {checkout.currency}</span>
              </div>
            )}
          </div>

          <div className="mt-6">
            {phase === 'form' && checkout?.provider === 'monri' && (
              <MonriCardForm
                checkout={checkout}
                language={language}
                error={error}
                setError={setError}
                onConfirmed={waitForWebhook}
                onRetry={reload}
                translate={translate}
              />
            )}

            {phase === 'form' && checkout?.clientSecret && checkout.provider !== 'monri' && (
              <Elements
                stripe={getStripe(checkout.publishableKey)}
                options={{
                  clientSecret: checkout.clientSecret,
                  locale: stripeLocale(language),
                  appearance: { theme: 'stripe', variables: { colorPrimary: '#A56C3B', borderRadius: '8px' } },
                }}
              >
                <CardForm
                  orderId={orderId}
                  amount={checkout.amount}
                  currency={checkout.currency}
                  error={error}
                  setError={setError}
                  onConfirmed={waitForWebhook}
                  translate={translate}
                />
              </Elements>
            )}

            {(phase === 'loading' || phase === 'checking') && (
              <div className="flex items-center gap-2 text-gray-600">
                <Spinner />
                {phase === 'checking' ? translate("Checking payment...") : translate("Processing...")}
              </div>
            )}

            {message && phase !== 'form' && (
              <p className={phase === 'processing' ? 'text-gray-700' : 'text-red-500'}>{translate(message)}</p>
            )}
          </div>
        </div>
      </div>
      <Footer categories={categories} />
    </div>
  );
}
